import {concatMap, defaultIfEmpty, from, map, Observable, of, switchMap, takeLast, tap} from 'rxjs';
import {Filter} from '@app/utils/_msf';
import {FilterOperator} from '@app/utils/filter-operator';
import {AiInferenceImageDataSource} from '@app/shared/data/ai-inference-image-data-source-uses-data';
import {FieldFilter} from '@6grain/masofa-monolith-api-client/ai-inference/model/fieldFilter';

/**
 * Columns GetTotalCount and GetCropIdentificationByBbox accept on CropIdentificationResult.
 * `regionId`, `fieldId`, `CropId` and `source` are not columns — the API answers 500
 * "Field with name … is not exist".
 */
export const CROP_IDENTIFICATION_DEFAULT_FILTERS: Filter[] = [
  {
    filterField: 'topProbability',
    filterOperator: FilterOperator.GreaterThan,
    filterValue: 0.59,
  },
];

const ID_PAGE_SIZE = 5000;
const FIELD_ID_CHUNK = 400;
const MAX_LOOKUP_IDS = 200_000;

export interface CropIdentificationSeasonLookup {
  regionId: string | null;
  fieldId: string | null;
  cropId: string | null;
}

export interface CropIdentificationFilterPlan {
  /** Safe to send as `filters` on CropIdentificationResult queries. */
  filters: Filter[];
  /** Region, field and culture are resolved to `seasonId` before the request. */
  seasonLookup: CropIdentificationSeasonLookup | null;
  /** Selected source is not `aiInferenceImageDataSource` (Satellite = 1, UAV = 2). */
  unsupportedSource: boolean;
}

export interface CropIdentificationIdPages {
  regionIdsIncludingChildren(regionId: string): Observable<string[]>;
  loadFieldIds(regionIds: string[], offset: number, take: number): Observable<Array<{id?: string | null}>>;
  loadSeasonIds(filters: Filter[], offset: number, take: number): Observable<Array<{id?: string | null}>>;
}

export function defaultCropIdentificationResultFilters(): Filter[] {
  return CROP_IDENTIFICATION_DEFAULT_FILTERS.map((filter) => ({...filter}));
}

/**
 * App `FilterOperator` is a numeric enum. Generated clients type the same integers
 * as a const union, so the operator is asserted at this boundary only.
 */
export function toApiFieldFilters(filters: Filter[]): FieldFilter[] {
  return filters.map((filter) => ({
    filterField: filter.filterField ?? null,
    filterOperator: filter.filterOperator as FieldFilter['filterOperator'],
    filterValue: filter.filterValue,
  }));
}

export function planCropIdentificationFilters(
  model: Record<string, unknown>,
): CropIdentificationFilterPlan {
  const filters = defaultCropIdentificationResultFilters();
  filters.push(...identificationDateFilters(model['identificationDate']));

  const sourceRaw = model['aiInferenceImageDataSource'] ?? model['source'];
  let unsupportedSource = false;
  if (hasFilterValue(sourceRaw)) {
    const source = toAiInferenceImageDataSource(sourceRaw);
    if (source == null) {
      unsupportedSource = true;
    } else {
      filters.push({
        filterField: 'aiInferenceImageDataSource',
        filterOperator: FilterOperator.Equals,
        filterValue: source,
      });
    }
  }

  const regionId = readFilterId(model['regionId']);
  const fieldId = readFilterId(model['fieldId']);
  const cropId = readFilterId(model['CropId'] ?? model['cropId']);
  const seasonLookup = regionId || fieldId || cropId ? {regionId, fieldId, cropId} : null;

  return {filters, seasonLookup, unsupportedSource};
}

export function chunkIds(ids: string[], size: number): string[][] {
  if (size < 1) {
    return ids.length ? [ids] : [];
  }
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size));
  }
  return chunks;
}

export function withSeasonIdFilter(filters: Filter[], seasonIds: string[]): Filter[] {
  if (!seasonIds.length) {
    return filters;
  }
  const unique = [...new Set(seasonIds)];
  return [
    ...filters,
    {
      filterField: 'seasonId',
      filterOperator: unique.length === 1 ? FilterOperator.Equals : FilterOperator.AnyOf,
      filterValue: unique.length === 1 ? unique[0] : unique,
    },
  ];
}

/**
 * Region-only 0 seasons is not a hard empty — bbox already scopes the viewport.
 * Field / culture with 0 seasons has no Result match.
 */
export function cropIaQueryAfterSeasonLookup(
  planFilters: Filter[],
  seasonIds: string[],
  lookup: CropIdentificationSeasonLookup,
): {filters: Filter[]; empty: boolean} {
  if (seasonIds.length) {
    return {
      filters: withSeasonIdFilter(planFilters, seasonIds),
      empty: false,
    };
  }
  return {
    filters: planFilters,
    empty: !!(lookup.fieldId || lookup.cropId),
  };
}

export function resolveCropIdentificationSeasonIds(
  lookup: CropIdentificationSeasonLookup,
  pages: CropIdentificationIdPages,
): Observable<string[]> {
  return resolveLookupFieldIds(lookup, pages).pipe(
    switchMap((fieldIds) => {
      if (fieldIds && !fieldIds.length) {
        return of([]);
      }
      const chunks = fieldIds == null ? [null] : chunkIds(fieldIds, FIELD_ID_CHUNK);
      return collectSeasonIdsFromChunks(chunks, lookup.cropId, pages);
    }),
  );
}

function resolveLookupFieldIds(
  lookup: CropIdentificationSeasonLookup,
  pages: CropIdentificationIdPages,
): Observable<string[] | null> {
  if (!lookup.regionId) {
    return of(lookup.fieldId ? [lookup.fieldId] : null);
  }
  return pages.regionIdsIncludingChildren(lookup.regionId).pipe(
    switchMap((regionIds) => {
      if (!regionIds.length) {
        return of([] as string[]);
      }
      return collectIds((offset, take) => pages.loadFieldIds(regionIds, offset, take)).pipe(
        map((ids) => (lookup.fieldId ? ids.filter((id) => id === lookup.fieldId) : ids)),
      );
    }),
  );
}

function collectSeasonIdsFromChunks(
  chunks: Array<string[] | null>,
  cropId: string | null,
  pages: CropIdentificationIdPages,
): Observable<string[]> {
  const seasonIds = new Set<string>();
  return from(chunks).pipe(
    concatMap((chunk) => {
      if (seasonIds.size > MAX_LOOKUP_IDS) {
        return of([] as string[]);
      }
      const filters = seasonQueryFilters(chunk, cropId);
      if (!filters.length) {
        return of([] as string[]);
      }
      return collectIds((offset, take) => pages.loadSeasonIds(filters, offset, take)).pipe(
        tap((ids) => {
          for (const id of ids) {
            seasonIds.add(id);
          }
        }),
      );
    }),
    takeLast(1),
    map(() => [...seasonIds]),
    defaultIfEmpty([]),
  );
}

function seasonQueryFilters(fieldIds: string[] | null, cropId: string | null): Filter[] {
  const filters: Filter[] = [];
  if (fieldIds?.length === 1) {
    filters.push({
      filterField: 'fieldId',
      filterOperator: FilterOperator.Equals,
      filterValue: fieldIds[0],
    });
  } else if (fieldIds && fieldIds.length > 1) {
    filters.push({
      filterField: 'fieldId',
      filterOperator: FilterOperator.AnyOf,
      filterValue: fieldIds,
    });
  }
  if (cropId) {
    filters.push({
      filterField: 'cropId',
      filterOperator: FilterOperator.Equals,
      filterValue: cropId,
    });
  }
  return filters;
}

function collectIds(
  load: (offset: number, take: number) => Observable<Array<{id?: string | null}>>,
): Observable<string[]> {
  const seen = new Set<string>();
  const page = (offset: number): Observable<string[]> =>
    load(offset, ID_PAGE_SIZE).pipe(
      map((batch) => batch ?? []),
      switchMap((batch) => {
        let added = 0;
        for (const row of batch) {
          const id = row?.id ? String(row.id) : '';
          if (!id || seen.has(id)) {
            continue;
          }
          seen.add(id);
          added++;
        }
        if (added === 0 || batch.length < ID_PAGE_SIZE || seen.size > MAX_LOOKUP_IDS) {
          return of([...seen]);
        }
        return page(offset + ID_PAGE_SIZE);
      }),
    );
  return page(0);
}

function hasFilterValue(value: unknown): boolean {
  if (value == null || value === '') {
    return false;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return toAiInferenceImageDataSource(value) != null;
  }
  return true;
}

export function identificationDateFilters(value: unknown): Filter[] {
  const bounds = identificationDateBounds(value);
  if (!bounds) {
    return [];
  }
  const filters: Filter[] = [];
  if (bounds.from) {
    filters.push({
      filterField: 'identificationDate',
      filterOperator: FilterOperator.GreaterThanOrEqual,
      filterValue: bounds.from,
    });
  }
  if (bounds.to) {
    filters.push({
      filterField: 'identificationDate',
      filterOperator: FilterOperator.LessThanOrEqual,
      filterValue: bounds.to,
    });
  }
  return filters;
}

function identificationDateBounds(value: unknown): {from: string | null; to: string | null} | null {
  if (value == null || value === '') {
    return null;
  }
  if (Array.isArray(value)) {
    const from = toIsoDay(value[0]);
    const to = toIsoDay(value[1] ?? value[0]);
    if (!from && !to) {
      return null;
    }
    if (from && to && from > to) {
      return null;
    }
    return {from, to};
  }
  const day = toIsoDay(value);
  if (!day) {
    return null;
  }
  return {from: day, to: day};
}

function toIsoDay(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
      return text.slice(0, 10);
    }
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : formatIsoDay(parsed);
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatIsoDay(value);
  }
  return null;
}

function formatIsoDay(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function readFilterId(value: unknown): string | null {
  const raw = unwrapId(value);
  if (typeof raw === 'string' || typeof raw === 'number') {
    const text = String(raw).trim();
    return text ? text : null;
  }
  return null;
}

function unwrapId(value: unknown): unknown {
  if (value && typeof value === 'object' && 'id' in value) {
    return (value as {id?: unknown}).id;
  }
  return value;
}

/** Crop identification stores image source as enum 1 (satellite) or 2 (UAV). */
export function toAiInferenceImageDataSource(value: unknown): AiInferenceImageDataSource | null {
  const raw = unwrapId(value);
  if (raw === AiInferenceImageDataSource.Satellite || raw === '1') {
    return AiInferenceImageDataSource.Satellite;
  }
  if (raw === AiInferenceImageDataSource.Uav || raw === '2') {
    return AiInferenceImageDataSource.Uav;
  }
  const text = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (text === 'satellite') {
    return AiInferenceImageDataSource.Satellite;
  }
  if (text === 'uav') {
    return AiInferenceImageDataSource.Uav;
  }
  return null;
}
